import React, { useState, useEffect } from 'react';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import DashboardCard from './components/DashboardCard';
import SectionDivider from './components/SectionDivider';
import PasswordGate from './components/PasswordGate';
import SearchBar from './components/SearchBar';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { 
  FiTrendingUp, FiCalendar, FiBookOpen, FiDollarSign, 
  FiMessageSquare, FiMap, FiRotateCw, FiTarget, 
  FiStar, FiImage, FiCompass, FiGlobe, FiEdit3, FiSettings, FiShield 
} = FiIcons;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    try {
      const auth = sessionStorage.getItem('padua_auth');
      if (auth === 'true') {
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('padua_auth');
    setIsAuthenticated(false);
  };

  const departments = [
    {
      id: 'executive',
      title: 'Executive',
      description: 'Leadership dashboards, company strategy, reporting, and high-level planning.',
      cards: [
        {
          title: "Branding & Executive Strategy",
          category: "Leadership / Brand Direction",
          description: "A broader strategic view of how Padua can strengthen positioning and support long-term growth.",
          link: "https://preview-6e25c9f2-a323-4faa-9dbd-46b8fc6c5e05.greta.questera.ai",
          icon: FiTrendingUp
        }
      ]
    },
    {
      id: 'sales',
      title: 'Sales Department',
      description: 'Everything related to inquiries, tours, estimates, pricing, follow-up, conversion, and booking.',
      cards: [
        {
          title: "Tour Booking",
          category: "Live Website / Sales",
          description: "The main private tour booking page for couples ready to schedule a visit.",
          link: "https://tour.paduaweddings.com",
          icon: FiCalendar
        },
        {
          title: "Wedding Guide & Pricing",
          category: "Live Website / Guide",
          description: "The planning guide and pricing overview created to help couples understand the Padua experience.",
          link: "https://guide.paduaweddings.com",
          icon: FiBookOpen
        },
        {
          title: "Starting Pricing & What to Expect",
          category: "Sales Tools / Pricing Experience",
          description: "A polished overview created to set clearer expectations around starting pricing and the Padua experience.",
          link: "https://timely-longma-58cf93.netlify.app/",
          icon: FiDollarSign
        },
        {
          title: "Curated Tour & Sales Experience",
          category: "Tour Experience / Conversion Strategy",
          description: "A concept page outlining how Padua can create a more immersive, polished, and high-touch tour experience that supports stronger sales.",
          link: "https://preview-80db47ec-771c-4491-983d-f09f0a9f7eab.greta.questera.ai",
          icon: FiMap
        },
        {
          title: "Lead Nurture Sequence",
          category: "Marketing Campaigns / Lead Follow-Up",
          description: "A strategic follow-up page built to help Padua nurture inquiries with better pacing and a smoother path toward tours.",
          link: "https://preview-0180f7a1-3cda-4c59-9c79-67da80c15bf4.greta.questera.ai",
          icon: FiRotateCw
        }
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing Department',
      description: 'Advertising, campaigns, SEO, social media, website growth, and seasonal marketing.',
      cards: [
        {
          title: "Social Media Ads + Drip Campaign Master Dashboard",
          category: "Marketing Campaigns / Campaign System",
          description: "A master planning hub for Padua’s paid media strategy, follow-up campaigns, and creative execution.",
          link: "https://preview-0c200999-ac4d-4b95-8a37-fe1932389a7e.greta.questera.ai",
          icon: FiTarget
        },
        {
          title: "Seasonal Plan: Fall 2026 – Winter 2027",
          category: "Promotions / Seasonal Campaigns",
          description: "A seasonal roadmap focused on promotional ideas and event opportunities for the upcoming holiday and winter seasons.",
          link: "https://superb-lebkuchen-893896.netlify.app",
          icon: FiCalendar
        },
        {
          title: "Fashion Show at Padua",
          category: "Experiential Marketing / Events",
          description: "A signature event concept designed to showcase vendors and position Padua as a visually compelling destination.",
          link: "https://preview-628f6e61-2954-4699-8265-0912462719a2.greta.questera.ai",
          icon: FiStar
        },
        {
          title: "Wedding Gallery",
          category: "Live Website / Portfolio",
          description: "A visual gallery showcasing Padua weddings, venue spaces, and real event inspiration.",
          link: "https://gallery.paduaweddings.com",
          icon: FiImage
        },
        {
          title: "Reviews Portal",
          category: "Live Website / Reviews",
          description: "The review collection portal used for past clients and wedding experience feedback.",
          link: "https://reviews.paduaweddings.com",
          icon: FiMessageSquare
        }
      ]
    },
    {
      id: 'creative',
      title: 'Brand & Creative',
      description: 'Brand identity, website concepts, creative direction, photography, storytelling, and long-term positioning.',
      cards: [
        {
          title: "Brand Strategy Foundation",
          category: "Brand Identity / Positioning",
          description: "Foundational direction for Padua’s visual tone, messaging, and overall brand alignment moving forward.",
          link: "https://preview-696c0e36-427a-4bd1-839b-ac46fb96ca8a.greta.questera.ai",
          icon: FiCompass
        },
        {
          title: "New Website Experience",
          category: "Website Experience / Brand Presence",
          description: "The latest website build designed to present Padua with greater clarity, elegance, and a stronger first impression.",
          link: "https://extraordinary-entremet-2bef5f.netlify.app",
          icon: FiGlobe
        },
        {
          title: "Padua Moments Brand Recommendation",
          category: "Brand Concepts / Naming Direction",
          description: "A concept page exploring Padua Moments as a refined brand direction, focusing on emotional resonance and storytelling.",
          link: "https://preview-19cf1a78-5d0a-434e-9024-93ad667d4a8c.greta.questera.ai",
          icon: FiEdit3
        }
      ]
    },
    {
      id: 'operations',
      title: 'Operations',
      description: 'Internal resources, vendors, SOPs, planning, calendars, and operational tools.',
      cards: []
    },
    {
      id: 'live',
      title: 'Live Websites',
      description: 'Direct access to the primary public-facing Padua web ecosystem.',
      cards: [
        {
          title: "Main Website",
          category: "Live / Public",
          description: "The official Padua Weddings homepage and primary brand destination.",
          link: "https://paduaweddings.com",
          icon: FiGlobe
        },
        {
          title: "Tour Booking",
          category: "Live / Sales",
          description: "The main private tour booking page for couples ready to schedule a visit.",
          link: "https://tour.paduaweddings.com",
          icon: FiCalendar
        },
        {
          title: "Wedding Guide",
          category: "Live / Guide",
          description: "The planning guide and pricing overview created to help couples understand the Padua experience.",
          link: "https://guide.paduaweddings.com",
          icon: FiBookOpen
        },
        {
          title: "Wedding Gallery",
          category: "Live / Portfolio",
          description: "A visual gallery showcasing Padua weddings, venue spaces, and real event inspiration.",
          link: "https://gallery.paduaweddings.com",
          icon: FiImage
        },
        {
          title: "Reviews Portal",
          category: "Live / Reviews",
          description: "The review collection portal used for past clients and wedding experience feedback.",
          link: "https://reviews.paduaweddings.com",
          icon: FiMessageSquare
        }
      ]
    }
  ];

  const filterCards = (cards) => {
    if (!searchQuery) return cards;
    const lowerQuery = searchQuery.toLowerCase();
    return cards.filter(card => 
      card.title.toLowerCase().includes(lowerQuery) ||
      card.description.toLowerCase().includes(lowerQuery) ||
      card.category.toLowerCase().includes(lowerQuery)
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#D9CCBA] border-t-[#6E7451] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <PasswordGate onAuthenticated={setIsAuthenticated} />;
  }

  return (
    <div className="min-h-screen bg-[#FCFAF6] text-[#2F2B28] font-sans selection:bg-[#D9CCBA] selection:text-[#2F2B28]">
      <Navigation onLogout={handleLogout} />
      
      <main>
        <Hero />
        
        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

        <div className="max-w-7xl mx-auto px-6 pb-24 space-y-16">
          {departments.map((dept, index) => {
            const filteredCards = filterCards(dept.cards);
            
            if (searchQuery && filteredCards.length === 0) return null;

            return (
              <React.Fragment key={dept.id}>
                <section id={dept.id} className="scroll-mt-24">
                  <div className="mb-10 text-center md:text-left max-w-2xl">
                    <h2 className="text-3xl font-serif text-[#2F2B28] mb-2">{dept.title}</h2>
                    <p className="text-[#2F2B28]/60 leading-relaxed">{dept.description}</p>
                  </div>
                  
                  {filteredCards.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredCards.map((card, cardIndex) => (
                        <DashboardCard key={`${dept.id}-${cardIndex}`} {...card} />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-[#F6F1E8]/50 border border-dashed border-[#D9CCBA] rounded-2xl p-12 text-center">
                      <SafeIcon icon={dept.id === 'operations' ? FiSettings : FiShield} className="w-8 h-8 text-[#D9CCBA] mx-auto mb-4" />
                      <p className="text-[#2F2B28]/40 font-serif italic">Resources for this department are currently being prepared.</p>
                    </div>
                  )}
                </section>
                {index < departments.length - 1 && <SectionDivider />}
              </React.Fragment>
            );
          })}
        </div>
      </main>

      <footer className="bg-[#2F2B28] text-[#FCFAF6] py-20 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h3 className="font-serif text-3xl md:text-4xl mb-6 text-[#F6F1E8] font-light leading-snug">
            “Built to help the Padua team review ideas with clarity, alignment, and momentum.”
          </h3>
          <div className="w-12 h-px bg-[#A67C52] mx-auto mb-8"></div>
          <p className="text-sm tracking-widest uppercase text-[#D9CCBA] font-medium">
            Padua Weddings Internal Review Dashboard
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;